import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useAuth } from "@/lib/auth-context";
import { authApi } from "@/lib/api";
import { Spinner } from "@/components/ui/spinner";
import { TwoFactorChallenge } from "./TwoFactorChallenge";
import {
  AlertCircle,
  Mail,
  Lock,
  User,
  ArrowLeft,
  LogIn,
  UserPlus,
  Eye,
  EyeOff,
  CheckCircle,
  X,
} from "lucide-react";
import { Link } from "react-router-dom";

export type AuthModalView = "login" | "register" | "forgot-password";

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Controlled mode: current view state (use with onViewChange) */
  currentView?: AuthModalView;
  /** Controlled mode: callback when view changes */
  onViewChange?: (view: AuthModalView) => void;
  /** Simple mode: initial view to show (if currentView not provided) */
  initialMode?: AuthModalView;
  /** Pre-fill email for registration (e.g., from invitation) */
  initialEmail?: string;
  /** Lock the email field (e.g., when accepting invitation) */
  emailLocked?: boolean;
  /** Callback when authentication succeeds (login or register verification email sent) */
  onAuthSuccess?: () => void;
  /** Disable automatic navigation to dashboard after login (default: true) */
  navigateAfterLogin?: boolean;
}

// Schemas
const loginSchema = z.object({
  email: z.string().email("Ogiltig e-postadress"),
  password: z.string().min(1, "Lösenord krävs"),
});

const registerSchema = z.object({
  email: z.string().email("Ogiltig e-postadress"),
  password: z
    .string()
    .min(10, "Lösenordet måste vara minst 10 tecken")
    .regex(/[A-Z]/, "Lösenordet måste innehålla minst en versal (A-Z)")
    .regex(/[a-z]/, "Lösenordet måste innehålla minst en gemen (a-z)")
    .regex(/[0-9]/, "Lösenordet måste innehålla minst en siffra (0-9)")
    .regex(/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/, "Lösenordet måste innehålla minst ett specialtecken"),
  firstName: z.string().min(1, "Förnamn krävs"),
  lastName: z.string().min(1, "Efternamn krävs"),
  acceptTerms: z.boolean().refine((val) => val === true, {
    message: "Du måste godkänna villkoren för att fortsätta",
  }),
});

const forgotPasswordSchema = z.object({
  email: z.string().email("Ogiltig e-postadress"),
});

type LoginFormData = z.infer<typeof loginSchema>;
type RegisterFormData = z.infer<typeof registerSchema>;
type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

// 2FA challenge state interface
interface TwoFactorChallengeState {
  challengeToken: string;
  email: string;
  firstName: string | null;
}

export function AuthModal({ isOpen, onClose, currentView: controlledView, onViewChange, initialMode = "login", initialEmail, emailLocked, onAuthSuccess, navigateAfterLogin = true }: AuthModalProps) {
  const navigate = useNavigate();
  const { login, register: registerUser } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [registerSuccess, setRegisterSuccess] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState("");
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState("");
  const [resendSuccess, setResendSuccess] = useState(false);
  const [twoFactorChallenge, setTwoFactorChallenge] = useState<TwoFactorChallengeState | null>(null);

  // Support both controlled and uncontrolled modes
  const [internalView, setInternalView] = useState<AuthModalView>(initialMode);
  const currentView = controlledView ?? internalView;

  // Reset internal view when modal opens with new initialMode
  useEffect(() => {
    if (isOpen && !controlledView) {
      setInternalView(initialMode);
    }
  }, [isOpen, initialMode, controlledView]);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [isOpen, onClose]);

  // Login form
  const loginForm = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  // Register form
  const registerForm = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      email: initialEmail || "",
      acceptTerms: false,
    },
  });

  // Update email field when initialEmail changes
  useEffect(() => {
    if (initialEmail) {
      registerForm.setValue("email", initialEmail);
      loginForm.setValue("email", initialEmail);
    }
  }, [initialEmail]);

  // Watch password for requirements
  const password = registerForm.watch("password") || "";
  const passwordRequirements = {
    minLength: password.length >= 10,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSpecialChar: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
  };
  const isPasswordValid = Object.values(passwordRequirements).every(Boolean);

  // Forgot password form
  const forgotPasswordForm = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const handleLogin = async (data: LoginFormData) => {
    setIsLoading(true);
    setError(null);

    const result = await login(data);

    if (result.success) {
      onAuthSuccess?.();
      onClose();
      if (navigateAfterLogin) {
        navigate("/dashboard");
      }
    } else if (result.requires2FA && result.challenge) {
      // 2FA required - show 2FA challenge view
      setTwoFactorChallenge({
        challengeToken: result.challenge.challengeToken,
        email: result.challenge.email,
        firstName: result.challenge.firstName,
      });
    } else if (result.errorCode === "EMAIL_NOT_VERIFIED") {
      setUnverifiedEmail(data.email);
      setEmailNotVerified(true);
      setResendSuccess(false);
    } else {
      setError(result.error || "Ett fel uppstod");
    }

    setIsLoading(false);
  };

  const handle2FASuccess = () => {
    setTwoFactorChallenge(null);
    onAuthSuccess?.();
    onClose();
    if (navigateAfterLogin) {
      navigate("/dashboard");
    }
  };

  const handle2FABack = () => {
    setTwoFactorChallenge(null);
    setError(null);
  };

  const handleRegister = async (data: RegisterFormData) => {
    if (!isPasswordValid) {
      setError("Lösenordet uppfyller inte alla krav.");
      return;
    }

    setIsLoading(true);
    setError(null);

    const result = await registerUser({
      ...data,
      acceptTerms: data.acceptTerms,
      acceptPrivacy: data.acceptTerms,
    });

    if (result.success) {
      setRegisteredEmail(data.email);
      setRegisterSuccess(true);
    } else {
      setError(result.error || "Ett fel uppstod");
    }

    setIsLoading(false);
  };

  const handleForgotPassword = async (data: ForgotPasswordFormData) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await authApi.forgotPassword(data.email);

      if (result.success) {
        setForgotPasswordSuccess(true);
      } else {
        // Ska aldrig hända (backend returnerar alltid success för anti-enumeration)
        setForgotPasswordSuccess(true);
      }
    } catch {
      // Visa ändå success för att inte läcka info om vilka e-postadresser som finns
      setForgotPasswordSuccess(true);
    }

    setIsLoading(false);
  };

  const handleResendVerification = async () => {
    setIsLoading(true);
    try {
      await authApi.resendVerification(unverifiedEmail);
      setResendSuccess(true);
    } catch {
      // Visa ändå success för att inte läcka info
      setResendSuccess(true);
    }
    setIsLoading(false);
  };

  const switchView = (view: AuthModalView) => {
    setError(null);
    setForgotPasswordSuccess(false);
    setRegisterSuccess(false);
    setEmailNotVerified(false);
    setResendSuccess(false);
    setShowPassword(false);
    loginForm.reset();
    registerForm.reset();
    forgotPasswordForm.reset();
    // Support both controlled and uncontrolled modes
    if (onViewChange) {
      onViewChange(view);
    } else {
      setInternalView(view);
    }
  };

  const handleClose = () => {
    setError(null);
    setForgotPasswordSuccess(false);
    setRegisterSuccess(false);
    setEmailNotVerified(false);
    setResendSuccess(false);
    setShowPassword(false);
    setTwoFactorChallenge(null);
    setInternalView(initialMode);
    loginForm.reset();
    registerForm.reset();
    forgotPasswordForm.reset();
    onClose();
  };

  if (!isOpen) return null;

  const getTitle = () => {
    switch (currentView) {
      case "login":
        return "Välkommen tillbaka";
      case "register":
        return "Skapa konto";
      case "forgot-password":
        return "Glömt lösenord?";
    }
  };

  const getSubtitle = () => {
    switch (currentView) {
      case "login":
        return "Logga in på ditt Kvanten-konto";
      case "register":
        return "Kom igång med Kvanten idag";
      case "forgot-password":
        return "Ange din e-postadress så skickar vi en återställningslänk";
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative bg-white text-gray-900 rounded-2xl shadow-2xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors z-10"
          aria-label="Stäng"
        >
          <X size={20} />
        </button>

        {/* Header */}
        <div className="pt-8 pb-6 px-8 text-center border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">{getTitle()}</h2>
          <p className="text-gray-500 mt-1">{getSubtitle()}</p>
        </div>

        {/* Content */}
        <div className="p-8">
          {/* Login View */}
          {currentView === "login" && (
            <div>
              {twoFactorChallenge ? (
                <TwoFactorChallenge
                  challengeToken={twoFactorChallenge.challengeToken}
                  email={twoFactorChallenge.email}
                  firstName={twoFactorChallenge.firstName}
                  onSuccess={handle2FASuccess}
                  onBack={handle2FABack}
                />
              ) : emailNotVerified ? (
                <div className="text-center">
                  <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Mail className="text-amber-600" size={32} />
                  </div>

                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    Verifiera din e-post
                  </h3>

                  <p className="text-gray-500 mb-2">
                    Kontot med e-postadressen:
                  </p>
                  <p className="font-medium text-gray-900 mb-4">{unverifiedEmail}</p>
                  <p className="text-gray-500 mb-6">
                    har inte verifierats än. Kontrollera din inkorg och klicka på verifieringslänken
                    för att aktivera ditt konto.
                  </p>

                  {resendSuccess ? (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                      <div className="flex items-center justify-center space-x-2 text-green-700">
                        <CheckCircle size={20} />
                        <p className="text-sm font-medium">
                          Ett nytt verifieringsmail har skickats!
                        </p>
                      </div>
                      <p className="text-sm text-green-600 mt-1">
                        Kontrollera din inkorg och skräppost.
                      </p>
                    </div>
                  ) : (
                    <div className="bg-primary-50 rounded-lg p-4 mb-6">
                      <p className="text-sm text-primary-800 mb-3">
                        Har du inte fått mailet? Kontrollera din skräppost eller begär ett nytt.
                      </p>
                      <button
                        onClick={handleResendVerification}
                        disabled={isLoading}
                        className="btn btn-secondary w-full flex items-center justify-center space-x-2 disabled:opacity-50"
                      >
                        {isLoading ? (
                          <Spinner size="sm" />
                        ) : (
                          <>
                            <Mail size={18} />
                            <span>Skicka nytt verifieringsmail</span>
                          </>
                        )}
                      </button>
                    </div>
                  )}

                  <button
                    onClick={() => {
                      setEmailNotVerified(false);
                      setResendSuccess(false);
                      setError(null);
                    }}
                    className="btn btn-primary w-full flex items-center justify-center space-x-2"
                  >
                    <ArrowLeft size={20} />
                    <span>Tillbaka till inloggning</span>
                  </button>
                </div>
              ) : (
                <>
                  {error && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3">
                      <AlertCircle className="text-red-600 mt-0.5 flex-shrink-0" size={20} />
                      <p className="text-sm text-red-600">{error}</p>
                    </div>
                  )}

                  <form onSubmit={loginForm.handleSubmit(handleLogin)} className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-2">
                        E-post
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                        <input
                          type="email"
                          {...loginForm.register("email")}
                          className="input pl-10"
                          placeholder="din@email.se"
                        />
                      </div>
                      {loginForm.formState.errors.email && (
                        <p className="text-sm text-red-600 mt-1">
                          {loginForm.formState.errors.email.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-2">
                        Lösenord
                      </label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                        <input
                          type="password"
                          {...loginForm.register("password")}
                          className="input pl-10"
                          placeholder="••••••••"
                        />
                      </div>
                      {loginForm.formState.errors.password && (
                        <p className="text-sm text-red-600 mt-1">
                          {loginForm.formState.errors.password.message}
                        </p>
                      )}
                    </div>

                    <div className="flex items-center justify-between">
                      <button
                        type="button"
                        onClick={() => switchView("forgot-password")}
                        className="text-sm text-primary-600 hover:text-primary-700"
                      >
                        Glömt lösenord?
                      </button>
                    </div>

                    <button
                      type="submit"
                      disabled={isLoading}
                      className="w-full btn btn-primary flex items-center justify-center space-x-2 disabled:opacity-50"
                    >
                      {isLoading ? (
                        <Spinner size="sm" />
                      ) : (
                        <>
                          <LogIn size={20} />
                          <span>Logga in</span>
                        </>
                      )}
                    </button>
                  </form>

                  <div className="mt-6 text-center">
                    <p className="text-sm text-gray-500">
                      Har du inget konto?{" "}
                      <button
                        onClick={() => switchView("register")}
                        className="text-primary-600 hover:text-primary-700 font-medium"
                      >
                        Registrera dig här
                      </button>
                    </p>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Register View */}
          {currentView === "register" && (
            <div>
              {registerSuccess ? (
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="text-green-600" size={32} />
                  </div>

                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    Kontrollera din e-post!
                  </h3>

                  <p className="text-gray-500 mb-2">
                    Vi har skickat ett verifieringsmail till:
                  </p>
                  <p className="font-medium text-gray-900 mb-6">{registeredEmail}</p>

                  <div className="bg-primary-50 rounded-lg p-4 mb-6">
                    <p className="text-sm text-primary-800">
                      Klicka på länken i mailet för att aktivera ditt konto.
                      Kontrollera din skräppost om du inte ser mailet inom några minuter.
                    </p>
                  </div>

                  <button
                    onClick={() => switchView("login")}
                    className="btn btn-primary w-full"
                  >
                    Gå till inloggning
                  </button>
                </div>
              ) : (
                <>
                  {error && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3">
                      <AlertCircle className="text-red-600 mt-0.5 flex-shrink-0" size={20} />
                      <p className="text-sm text-red-600">{error}</p>
                    </div>
                  )}

                  <form onSubmit={registerForm.handleSubmit(handleRegister)} className="space-y-5">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-900 mb-2">
                          Förnamn
                        </label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                          <input
                            type="text"
                            {...registerForm.register("firstName")}
                            className="input pl-10"
                            placeholder="Johan"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-900 mb-2">
                          Efternamn
                        </label>
                        <input
                          type="text"
                          {...registerForm.register("lastName")}
                          className="input"
                          placeholder="Andersson"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-2">
                        E-post
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                        <input
                          type="email"
                          {...registerForm.register("email")}
                          className={`input pl-10 ${emailLocked ? "bg-gray-100 text-gray-500" : ""}`}
                          placeholder="din@email.se"
                          readOnly={emailLocked}
                        />
                      </div>
                      {emailLocked && (
                        <p className="text-xs text-gray-500 mt-1">
                          E-postadressen är kopplad till din inbjudan
                        </p>
                      )}
                      {registerForm.formState.errors.email && (
                        <p className="text-sm text-red-600 mt-1">
                          {registerForm.formState.errors.email.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-2">
                        Lösenord
                      </label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                        <input
                          type={showPassword ? "text" : "password"}
                          {...registerForm.register("password")}
                          className="input pl-10 pr-10"
                          placeholder="Minst 8 tecken"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-900"
                        >
                          {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                        </button>
                      </div>

                      {password.length > 0 && (
                        <div className="mt-3 p-3 bg-gray-100 rounded-lg">
                          <p className="text-xs text-gray-500 font-medium mb-2">
                            Lösenordet måste innehålla:
                          </p>
                          <ul className="text-xs space-y-1">
                            <li className={`flex items-center space-x-2 ${passwordRequirements.minLength ? "text-green-600" : "text-gray-500"}`}>
                              <span>{passwordRequirements.minLength ? "✓" : "○"}</span>
                              <span>Minst 10 tecken</span>
                            </li>
                            <li className={`flex items-center space-x-2 ${passwordRequirements.hasUppercase ? "text-green-600" : "text-gray-500"}`}>
                              <span>{passwordRequirements.hasUppercase ? "✓" : "○"}</span>
                              <span>Minst en versal (A-Z)</span>
                            </li>
                            <li className={`flex items-center space-x-2 ${passwordRequirements.hasLowercase ? "text-green-600" : "text-gray-500"}`}>
                              <span>{passwordRequirements.hasLowercase ? "✓" : "○"}</span>
                              <span>Minst en gemen (a-z)</span>
                            </li>
                            <li className={`flex items-center space-x-2 ${passwordRequirements.hasNumber ? "text-green-600" : "text-gray-500"}`}>
                              <span>{passwordRequirements.hasNumber ? "✓" : "○"}</span>
                              <span>Minst en siffra (0-9)</span>
                            </li>
                            <li className={`flex items-center space-x-2 ${passwordRequirements.hasSpecialChar ? "text-green-600" : "text-gray-500"}`}>
                              <span>{passwordRequirements.hasSpecialChar ? "✓" : "○"}</span>
                              <span>Minst ett specialtecken (!@#$% etc.)</span>
                            </li>
                          </ul>
                        </div>
                      )}
                    </div>

                    <div className="flex items-start space-x-3">
                      <input
                        type="checkbox"
                        id="acceptTerms"
                        {...registerForm.register("acceptTerms")}
                        className="mt-1 h-4 w-4 text-primary-600 border-gray-200 rounded focus:ring-primary-500"
                      />
                      <label htmlFor="acceptTerms" className="text-sm text-gray-500">
                        Jag har läst och godkänner{" "}
                        <Link
                          to="/villkor"
                          target="_blank"
                          className="text-primary-600 hover:text-primary-700 underline"
                        >
                          användarvillkoren
                        </Link>{" "}
                        och{" "}
                        <Link
                          to="/integritetspolicy"
                          target="_blank"
                          className="text-primary-600 hover:text-primary-700 underline"
                        >
                          integritetspolicyn
                        </Link>
                      </label>
                    </div>
                    {registerForm.formState.errors.acceptTerms && (
                      <p className="text-sm text-red-600 -mt-3">
                        {registerForm.formState.errors.acceptTerms.message}
                      </p>
                    )}

                    <button
                      type="submit"
                      disabled={isLoading || !isPasswordValid}
                      className="w-full btn btn-primary flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isLoading ? (
                        <Spinner size="sm" />
                      ) : (
                        <>
                          <UserPlus size={20} />
                          <span>Skapa konto</span>
                        </>
                      )}
                    </button>
                  </form>

                  <div className="mt-6 text-center">
                    <p className="text-sm text-gray-500">
                      Har du redan ett konto?{" "}
                      <button
                        onClick={() => switchView("login")}
                        className="text-primary-600 hover:text-primary-700 font-medium"
                      >
                        Logga in här
                      </button>
                    </p>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Forgot Password View */}
          {currentView === "forgot-password" && (
            <div>
              {forgotPasswordSuccess ? (
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="text-green-600" size={32} />
                  </div>

                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    Kontrollera din e-post
                  </h3>

                  <p className="text-gray-500 mb-6">
                    Om det finns ett konto kopplat till den e-postadressen har vi skickat
                    instruktioner för att återställa ditt lösenord.
                  </p>

                  <p className="text-sm text-gray-500 mb-6">
                    Länken är giltig i 1 timme. Kontrollera din skräppost om du inte ser mailet.
                  </p>

                  <button
                    onClick={() => switchView("login")}
                    className="btn btn-primary w-full flex items-center justify-center space-x-2"
                  >
                    <ArrowLeft size={20} />
                    <span>Tillbaka till inloggning</span>
                  </button>
                </div>
              ) : (
                <>
                  <button
                    onClick={() => switchView("login")}
                    className="flex items-center space-x-1 text-sm text-primary-600 hover:text-primary-700 font-medium mb-6"
                  >
                    <ArrowLeft size={16} />
                    <span>Tillbaka till inloggning</span>
                  </button>

                  {error && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3">
                      <AlertCircle className="text-red-600 mt-0.5 flex-shrink-0" size={20} />
                      <p className="text-sm text-red-600">{error}</p>
                    </div>
                  )}

                  <form
                    onSubmit={forgotPasswordForm.handleSubmit(handleForgotPassword)}
                    className="space-y-6"
                  >
                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-2">
                        E-post
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                        <input
                          type="email"
                          {...forgotPasswordForm.register("email")}
                          className="input pl-10"
                          placeholder="din@email.se"
                          autoFocus
                        />
                      </div>
                      {forgotPasswordForm.formState.errors.email && (
                        <p className="text-sm text-red-600 mt-1">
                          {forgotPasswordForm.formState.errors.email.message}
                        </p>
                      )}
                    </div>

                    <button
                      type="submit"
                      disabled={isLoading}
                      className="w-full btn btn-primary flex items-center justify-center space-x-2 disabled:opacity-50"
                    >
                      {isLoading ? <Spinner size="sm" /> : <span>Skicka återställningslänk</span>}
                    </button>
                  </form>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
